import React, { useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useMutation, useQuery } from 'hooks/apollo';
import RoutedLayout from 'components/Layout/RoutedLayout';
import { Button, Form, Input } from 'antd';
import ModalForm from 'components/ModalForm';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import { EDIT_DASHBOARD } from 'shared/constants/permissions';
import useUser from 'hooks/useUser';
import useDataSource from 'hooks/useDataSource';
import useCheckPermission from 'hooks/useCheckPermission';
import { getDashboardRoutes } from './utils';

const ANALYTICS_GOALS_FIELDS = `
  website {
    id
    matomoId
    matomoGoals(dateRanges: [{ predefined: LAST_30_DAYS }]) {
      id
      matomoGoalId
      name
      conversions
    }
  }
`;

const QUERY = gql`
  query DashboardPage($key: String!) {
    dashboardsPage(key: $key) {
      id
      title
      dashboards {
        id
        title
        parent
        settings
        contents { id }
      }
    }
  }
`;

const CREATE_DASHBOARD_MUTATION = gql`
  mutation CreateDashboard($dashboardPageId: ID!, $input: DashboardInput!) {
    createDashboard(dashboardPageId: $dashboardPageId, input: $input) {
      id
    }
  }
`;

const DashboardPage = ({ dashboardPageKey, canEditParent, beforeMenu, persistKey, className, extraRoutes }) => {
  const { data, loading, refetch } = useQuery(QUERY, {
    variables: { key: dashboardPageKey },
  });
  const dashboardPage = data?.dashboardsPage;
  const loadMatomoGoals = dashboardPage?.dashboards?.some(d => d.settings?.dynamicContent === 'MATOMO_GOALS');

  const { dataSource } = useDataSource({
    extraFields: loadMatomoGoals ? ANALYTICS_GOALS_FIELDS : undefined,
  });

  const [createDashboard] = useMutation(CREATE_DASHBOARD_MUTATION);
  const [createDashboardOpen, setCreateDashboardOpen] = useState(false);
  const history = useHistory();
  const { url } = useRouteMatch();
  const { hasPermission } = useUser();
  const checkPermission = useCheckPermission();

  const canEdit = hasPermission(EDIT_DASHBOARD);

  const routes = useMemo(() => [
    ...getDashboardRoutes({
      dashboardPage,
      props: {
        onChange: () => refetch(),
        onDelete: () => refetch(),
        canEditParent,
      },
      canEdit,
      matomoGoals: dataSource?.website?.matomoGoals,
      checkPermission,
    }),
    ...(extraRoutes || []),
  ], [dashboardPage, dataSource?.website?.matomoGoals, canEdit, checkPermission, extraRoutes]);

  const onCreateDashboard = async (values) => createDashboard({ variables: { dashboardPageId: dashboardPage.id, input: { ...values } } });

  const onDashboardCreated = async (res) => {
    setCreateDashboardOpen(false);
    await refetch();
    history.push(`${url}/${res.data.createDashboard.id}`);
  };

  const afterMenu = canEdit && dashboardPage ? (
    <div style={{ margin: 4, marginTop: 16 }}>
      <Button block icon={<PlusOutlined />} onClick={() => setCreateDashboardOpen(true)}>Create Dashboard</Button>
      <ModalForm
        title="Create Dashboard"
        open={createDashboardOpen}
        onCancel={() => setCreateDashboardOpen(false)}
        onDone={onDashboardCreated}
        onSubmit={onCreateDashboard}
        okText="Create"
      >
        <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Title is required' }]}>
          <Input />
        </Form.Item>
      </ModalForm>
    </div>
  ) : null;

  return (
    <RoutedLayout className={className} sidebar={{ afterMenu, beforeMenu }} routes={routes} loading={loading} persistKey={persistKey} />
  );
};

export default DashboardPage;
